import type { SanityLinkValue, SanityRichTextValue } from "@ouster/shared";

import { adaptSanityLink } from "../link";

type PortableTextChild = {
  _key?: string;
  _type?: string;
  marks?: string[];
  text?: string;
};

type PortableTextMarkDef = SanityLinkValue & {
  _key?: string;
  _type?: string;
};

type PortableTextBlock = {
  _key?: string;
  _type?: string;
  children?: PortableTextChild[];
  level?: number;
  listItem?: string;
  markDefs?: PortableTextMarkDef[];
  style?: string;
};

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replace(/`/g, "&#96;");
}

function getBlockTag(style?: string) {
  switch (style) {
    case "h2":
    case "wild.richtext.style.h2":
      return "h2";
    case "h3":
    case "wild.richtext.style.h3":
      return "h3";
    case "h4":
    case "wild.richtext.style.h4":
      return "h4";
    case "h6":
    case "wild.richtext.style.h6":
      return "h6";
    case "blockquote":
      return "blockquote";
    default:
      return "p";
  }
}

function renderLinkMark(markDef: PortableTextMarkDef, children: string) {
  const link = adaptSanityLink(markDef);

  if (!link?.href) {
    return children;
  }

  const attrs = [
    `href="${escapeAttribute(link.href)}"`,
    link.newWindow ? `target="_blank"` : undefined,
    link.newWindow ? `rel="noopener"` : undefined,
    link.download ? `download` : undefined,
  ].filter(Boolean);

  return `<a ${attrs.join(" ")}>${children}</a>`;
}

function renderMark(mark: string, markDefs: PortableTextMarkDef[], children: string) {
  const markDef = markDefs.find((item) => item._key === mark);

  if (markDef) {
    return renderLinkMark(markDef, children);
  }

  switch (mark) {
    case "strong":
    case "wild.richtext.decorator.strong":
      return `<strong>${children}</strong>`;
    case "em":
    case "wild.richtext.decorator.em":
      return `<em>${children}</em>`;
    case "code":
    case "wild.richtext.decorator.code":
      return `<code>${children}</code>`;
    case "underline":
      return `<u>${children}</u>`;
    case "strike-through":
      return `<s>${children}</s>`;
    case "sup":
    case "wild.richtext.decorator.sup":
      return `<sup>${children}</sup>`;
    default:
      return children;
  }
}

/**
 * Portable Text stores a Shift+Enter soft break as a literal "\n" inside the span. Raw
 * newlines collapse to a single space in HTML, so the break has to become a <br /> to
 * survive.
 *
 * Only breaks with real text on both sides qualify. Craft-migrated content carries 738
 * whitespace-only spans across 137 published pages whose entire text is "\n"; those must
 * keep collapsing or every one of those pages gains a stray line break.
 */
function withSoftBreaks(html: string) {
  return html.replace(/(\S[^\S\n]*)\n([^\S\n]*\S)/g, "$1<br />$2");
}

function renderChild(child: PortableTextChild, markDefs: PortableTextMarkDef[]) {
  const text = withSoftBreaks(escapeHtml(child.text || ""));

  return (child.marks || []).reduce((value, mark) => renderMark(mark, markDefs, value), text);
}

function renderBlock(block: PortableTextBlock) {
  const children = (block.children || []).map((child) => renderChild(child, block.markDefs || [])).join("");

  if (!children) {
    return "";
  }

  const tag = getBlockTag(block.style);
  return `<${tag}>${children}</${tag}>`;
}

function listTag(item: PortableTextBlock): "ol" | "ul" {
  return item.listItem === "number" ? "ol" : "ul";
}

/**
 * Render a run of consecutive list blocks into nested `<ul>`/`<ol>` markup, honoring each block's
 * `level` (Sanity's portable-text nesting convention: a level-2 item belongs inside the preceding
 * level-1 item's `<li>`). The list type at each depth is taken from the first item at that depth,
 * so a numbered sublist inside a bulleted list renders correctly.
 */
function renderList(items: PortableTextBlock[]) {
  const levelOf = (item: PortableTextBlock) => item.level || 1;

  // Recursively consume items at `level` (and deeper). Returns the rendered list and the index of
  // the first item that belongs to a shallower level.
  function build(start: number, level: number): { html: string; next: number } {
    const first = items[start];
    const tag = first ? listTag(first) : "ul";
    let body = "";
    let index = start;

    while (index < items.length) {
      const item = items[index]!;
      if (levelOf(item) < level) break;

      // A deeper item with no parent at this level (malformed nesting): wrap it in its own list.
      if (levelOf(item) > level) {
        const nested = build(index, levelOf(item));
        body += nested.html;
        index = nested.next;
        continue;
      }

      const content = (item.children || []).map((child) => renderChild(child, item.markDefs || [])).join("");
      index += 1;

      // Any immediately-following deeper items nest inside this item's <li>.
      let nestedHtml = "";
      const after = items[index];
      if (after && levelOf(after) > level) {
        const nested = build(index, levelOf(after));
        nestedHtml = nested.html;
        index = nested.next;
      }

      if (content || nestedHtml) {
        body += `<li>${content}${nestedHtml}</li>`;
      }
    }

    return { html: body ? `<${tag}>${body}</${tag}>` : "", next: index };
  }

  return build(0, items[0] ? items[0].level || 1 : 1).html;
}

export function sanityRichTextToHtml(value?: SanityRichTextValue | null) {
  if (!Array.isArray(value)) {
    return "";
  }

  const html: string[] = [];
  let listItems: PortableTextBlock[] = [];

  function flushList() {
    if (listItems.length > 0) {
      html.push(renderList(listItems));
    }

    listItems = [];
  }

  for (const block of value as PortableTextBlock[]) {
    if (block._type !== "block") {
      continue;
    }

    if (block.listItem) {
      listItems.push(block);
      continue;
    }

    flushList();
    html.push(renderBlock(block));
  }

  flushList();

  return html.filter(Boolean).join("");
}

export function toHtml(value?: SanityRichTextValue | null) {
  const html = sanityRichTextToHtml(value);
  return html || undefined;
}

export function toCraftRichText(value?: SanityRichTextValue | null) {
  return toHtml(value);
}

/**
 * Flatten portable text down to a single plain-text string — for consumers
 * that inherited a plain-string field (e.g. ProductShowcase.subtitle) but now
 * receive rich text from a `subline` rename.
 */
export function toPlainText(value?: SanityRichTextValue | null): string | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const text = (value as PortableTextBlock[])
    .map((block) => {
      if (!block || block._type !== "block") return "";
      const children = Array.isArray(block.children) ? block.children : [];
      return children.map((child) => (typeof child.text === "string" ? child.text : "")).join("");
    })
    .filter(Boolean)
    .join("\n");
  return text || undefined;
}
